"use client";

import { useState } from "react";

import BoxReveal from "@cohub42/ui/components/magicui/box-reveal";
import WordPullUp from "@cohub42/ui/components/magicui/word-pull-up";

const testimonials = [
  {
    name: "Sarah Chen",
    role: "Co-Founder, TechFlow AI",
    image: "/images/users/sarah.jpg",
    content:
      "Found my technical co-founder within 2 weeks on CoHub42. The swipe-based matching made it easy to find someone with complementary skills. We connected instantly through the real-time chat and started building our SaaS platform together!",
    company: "TechFlow AI",
    stats: "$2.3M Seed Round",
  },
  {
    name: "Alex Rodriguez",
    role: "Co-Founder, TechVentures",
    image: "/images/users/alex.jpg",
    content:
      "As a non-technical founder, I used CoHub42's Launch Hub resources and networking events to learn the ecosystem, then found the perfect CTO through the AI matching. The mentorship programs gave me the confidence to pitch our idea.",
    company: "TechVentures",
    stats: "Y Combinator S23",
  },
  {
    name: "Priya Sharma",
    role: "Co-Founder, HealthSync",
    image: "/images/users/priya.jpg",
    content:
      "The AI Assistant helped me refine my startup strategy before I even started matching. After filtering by skills and location, I found my co-founder in 3 conversations. The meetups feature also connected us with our first advisors.",
    company: "HealthSync",
    stats: "Pre-Seed: $850K",
  },
  {
    name: "James Wilson",
    role: "Co-Founder, FinTechPro",
    image: "/images/users/james.jpg",
    content:
      "After months of searching traditional networks, CoHub42's AI matched me with a co-founder who had exactly the fintech experience I needed. The group chat feature made it easy to bring in our third co-founder too.",
    company: "FinTechPro",
    stats: "Series A: $5.2M",
  },
];

export default function CoFounderTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="relative overflow-hidden py-24 sm:py-32">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <BoxReveal boxColor={"transparent"} duration={0.5} width="100%">
            <div className="mb-4 inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 ring-1 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-800">
              🎉 Success Stories
            </div>
          </BoxReveal>

          <WordPullUp
            className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl"
            words="Founders Who Found Their Perfect Match"
          />

          <BoxReveal boxColor={"transparent"} duration={0.5} width="100%">
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Real stories from entrepreneurs who found their co-founders on
              CoHub42 and built successful companies together.
            </p>
          </BoxReveal>
        </div>

        <div className="mx-auto mt-16 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none">
          <div className="-mt-8 columns-1 sm:columns-2 lg:columns-2 xl:columns-2">
            {testimonials.map((testimonial, index) => (
              <BoxReveal
                key={testimonial.name}
                boxColor={"transparent"}
                duration={0.5 + index * 0.1}
                width="100%"
              >
                <div className="mb-8 break-inside-avoid pt-8">
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:bg-gray-800">
                    <div className="flex flex-col">
                      {/* Quote */}
                      <div className="mb-4">
                        <svg
                          className="h-8 w-8 text-primary/20"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      </div>

                      {/* Content */}
                      <blockquote className="text-gray-900 dark:text-white">
                        <p className="text-sm leading-6">
                          "{testimonial.content}"
                        </p>
                      </blockquote>

                      {/* Footer */}
                      <figcaption className="mt-6 flex items-center gap-x-4 border-t border-gray-200 pt-6 dark:border-gray-700">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/20 font-bold text-white">
                          {testimonial.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>

                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {testimonial.name}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {testimonial.role}
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-xs font-medium text-primary">
                              {testimonial.company}
                            </span>
                            <span className="text-xs text-gray-500">
                              • {testimonial.stats}
                            </span>
                          </div>
                        </div>
                      </figcaption>
                    </div>
                  </div>
                </div>
              </BoxReveal>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="relative mx-auto mt-16 max-w-7xl">
          <BoxReveal boxColor={"transparent"} duration={0.8} width="100%">
            <div className="grid grid-cols-2 gap-8 text-center lg:grid-cols-4">
              {[
                {
                  label: "Active Founders",
                  value: "1,000+",
                  color: "text-blue-600",
                },
                {
                  label: "Successful Matches",
                  value: "150+",
                  color: "text-green-600",
                },
                {
                  label: "Startups Launched",
                  value: "10+",
                  color: "text-purple-600",
                },
                {
                  label: "Match Rate",
                  value: "94%",
                  color: "text-orange-600",
                },
              ].map((stat) => (
                <div key={stat.label} className="group">
                  <div
                    className={`text-4xl font-bold ${stat.color} transition-all duration-300 group-hover:scale-110`}
                  >
                    {stat.value}
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </BoxReveal>
        </div>
      </div>
    </div>
  );
}
